import React from "react";
import StrategyIcon from "./StrategyIcon";
import { Commend3Strategy } from "../Types";
import {
  generationUsageLabel,
  isGenerationLimitReached,
  isStrategyGenerated,
  isStrategyGenerating,
  resolveGenerationLine,
} from "../Commend3Helper";
import { GENERATION_LIMIT_NOTE, PREVIEW_DISABLED_REASON } from "../Commend3Constants";

type StrategyCardProps = {
  strategy: Commend3Strategy;
  index: number;
  generateEnabled: boolean;
  generateUnavailableReason?: string;
  onGenerate?: (strategy: Commend3Strategy) => void;
  onPreview: (strategy: Commend3Strategy) => void;
};

const TONE_COLORS: Record<string, string> = {
  fresh: "#109648",
  stale: "#D97706",
  muted: "#B4B9D4",
};

const WarnIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="tw-mt-px tw-h-[13px] tw-w-[13px] tw-shrink-0"
    style={{
      stroke: "#B45309",
      fill: "none",
      strokeWidth: 1.8,
      strokeLinecap: "round",
      strokeLinejoin: "round",
    }}
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v4M12 16h.01" />
  </svg>
);

const StrategyCard = ({
  strategy,
  index,
  generateEnabled,
  generateUnavailableReason,
  onGenerate,
  onPreview,
}: StrategyCardProps) => {
  const line = resolveGenerationLine(strategy);
  const generating = isStrategyGenerating(strategy);
  const capped = isGenerationLimitReached(strategy);
  const previewOff = !isStrategyGenerated(strategy);
  const generateOff = !generateEnabled || generating || capped;
  const description = strategy?.tool_tip_message?.trim();

  return (
    <div
      id={`commend3-strategy-card-${index}`}
      className="tw-shrink-0 tw-rounded-xl tw-bg-white"
      style={{ border: "0.5px solid #E3E5EC" }}
    >
      <div className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-[15px]">
        <div
          className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md"
          style={{ background: "#EAF0FF", color: "#0B0C23" }}
        >
          <StrategyIcon name={strategy.name} type={strategy.type} />
        </div>

        <div className="tw-min-w-0 tw-flex-1">
          <div className="tw-mb-[3px] tw-flex tw-items-center tw-gap-[5px] tw-text-[13px] tw-font-medium">
            {strategy.name}
            {description ? (
              <span
                title={description}
                className="tw-inline-flex tw-h-4 tw-w-4 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-[9px] tw-font-semibold tw-text-[#59596E]"
                style={{ border: "1.5px solid #B4B9D4", cursor: "default" }}
                aria-label={`About ${strategy.name}`}
              >
                i
              </span>
            ) : null}
          </div>

          <div
            className="tw-mb-0.5 tw-text-[12px] tw-font-medium"
            style={{ color: TONE_COLORS[line.tone] ?? TONE_COLORS.muted }}
          >
            {line.text}
          </div>

          <div
            className="tw-text-[11px]"
            style={{ color: capped ? "#B45309" : "#B4B9D4", fontWeight: capped ? 500 : 400 }}
          >
            {generationUsageLabel(strategy)}
          </div>

          {capped ? (
            <div
              className="tw-mt-1.5 tw-flex tw-items-start tw-gap-1.5 tw-px-[9px] tw-py-[7px]"
              style={{
                background: "#FEF3C7",
                borderLeft: "2.5px solid #D97706",
                borderRadius: "0 4px 4px 0",
              }}
            >
              <WarnIcon />
              <span className="tw-text-[11.5px] tw-text-[#B45309]" style={{ lineHeight: 1.5 }}>
                {GENERATION_LIMIT_NOTE}
              </span>
            </div>
          ) : null}
        </div>

        <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-[7px]">
          <span
            className="tw-inline-flex tw-shrink-0"
            title={generateOff ? generateUnavailableReason : undefined}
          >
            <button
              id={`commend3-strategy-${index}-generate`}
              type="button"
              className="tw-inline-flex tw-items-center tw-gap-[5px] tw-rounded-lg tw-bg-white tw-px-[11px] tw-py-[5px] tw-text-[12px] tw-font-medium"
              style={{
                border: "0.5px solid #E3E5EC",
                color: "#030562",
                opacity: generateOff ? 0.45 : 1,
                cursor: generateOff ? "not-allowed" : "pointer",
              }}
              disabled={generateOff}
              onClick={() => onGenerate && onGenerate(strategy)}
            >
              {generating ? "Generating…" : isStrategyGenerated(strategy) ? "Regenerate" : "Generate"}
            </button>
          </span>

          <span
            className="tw-inline-flex tw-shrink-0"
            title={previewOff ? PREVIEW_DISABLED_REASON : undefined}
          >
            <button
              id={`commend3-strategy-${index}-preview`}
              type="button"
              className="tw-inline-flex tw-items-center tw-gap-[5px] tw-rounded-md tw-bg-white tw-px-[11px] tw-py-[5px] tw-text-[12px] tw-font-medium"
              style={{
                border: "0.5px solid #E3E5EC",
                color: "#0B0C23",
                opacity: previewOff ? 0.45 : 1,
                cursor: previewOff ? "not-allowed" : "pointer",
              }}
              disabled={previewOff}
              onClick={() => onPreview(strategy)}
            >
              Preview
            </button>
          </span>
        </div>
      </div>
    </div>
  );
};

export default StrategyCard;
